import pkg from '../../package.json';
import api from './api';
import logger from './logger';
import utils from './utils';

function buildUrlWithQuery(url, query) {
  try {
    if (!url || !query || typeof query !== 'object') return url;
    const hasQueryAlready = url.includes('?');
    if (hasQueryAlready) return url;
    const params = new URLSearchParams();
    Object.keys(query).forEach(k => {
      const v = query[k];
      if (v === undefined || v === null) return;
      if (Array.isArray(v)) v.forEach(item => params.append(k, String(item)));
      else params.append(k, String(v));
    });
    const qs = params.toString();
    return qs ? `${url}?${qs}` : url;
  } catch {
    return url;
  }
}

function extractApiFailureContext(err) {
  if (!err || typeof err !== 'object') return null;

  // If upstream already produced ClickUp/mobile-shaped fields, prefer them verbatim.
  if (
    err.failedHttpMethod ||
    err.failedHttpUrl ||
    err.failedHttpRequestBodyFull
  ) {
    return {
      method: err.failedHttpMethod,
      url: err.failedHttpUrl,
      body: err.failedHttpRequestBodyFull
    };
  }

  // Common wrapper shape in app: { err: <api error>, ...context }
  if (err.err && typeof err.err === 'object') {
    const nested = extractApiFailureContext(err.err);
    if (nested) return nested;
  }

  if (err.api_request && typeof err.api_request === 'object') {
    const { method, url, body } = err.api_request;
    if (method || url || body !== undefined) {
      return { method, url, body };
    }
  }

  // superagent errors often include { method, url } directly, and sometimes
  // have additional request metadata on response/request objects.
  const method =
    err.method ||
    err?.request?.method ||
    err?.response?.req?.method ||
    err?.response?.request?.method;

  const rawUrl =
    err.url ||
    err?.request?.url ||
    err?.response?.req?.url ||
    err?.response?.request?.url;

  const query =
    err.qs ||
    err.query ||
    err?.request?.qs ||
    err?.request?.query ||
    err?.response?.req?.qs ||
    err?.response?.request?.qs;

  const url = buildUrlWithQuery(rawUrl, query);

  // superagent stores JSON bodies on _data; some codepaths may store on body/data
  const body =
    err?.request?._data ??
    err?.request?.data ??
    err?.request?.body ??
    err?.response?.req?._data ??
    err?.response?.request?._data ??
    err?.response?.req?.body ??
    err?.response?.request?.body;

  if (!method && !url && body === undefined) return null;

  return {
    method: method ? String(method).toUpperCase() : undefined,
    url,
    body
  };
}

function stringifyRequestBodyFull(body) {
  if (body === undefined) return undefined;
  if (body === null) return 'null';
  if (typeof body === 'string') return body;
  try {
    return JSON.stringify(body);
  } catch {
    try {
      return utils.recursiveStringify(body);
    } catch {
      return String(body);
    }
  }
}

function stripApiRequestMetaForErrorPayload(value) {
  const STRIP_KEYS = new Set([
    'api_request',
    'failedHttpMethod',
    'failedHttpUrl',
    'failedHttpRequestBodyFull'
  ]);

  const seen = new WeakSet();

  const walk = v => {
    if (!v || typeof v !== 'object') return v;
    if (seen.has(v)) return '[Circular]';
    seen.add(v);

    if (Array.isArray(v)) return v.map(walk);

    const out = {};
    Object.keys(v).forEach(k => {
      if (STRIP_KEYS.has(k)) return;
      out[k] = walk(v[k]);
    });
    return out;
  };

  return walk(value);
}

const errorReportHelper = {
  trySubmitError(userMessage, err) {
    return new Promise(resolve => {
      try {
        let message = {
          type: 'js-error',
          msg: userMessage || '- no message -',
          error: utils.recursiveStringify(
            stripApiRequestMetaForErrorPayload(err)
          ),
          app_version: pkg.version
        };

        const apiCtx = extractApiFailureContext(err);
        if (apiCtx) {
          // Fields expected by ClickUp/mobile bug formatting
          message.failedHttpMethod = apiCtx.method;
          message.failedHttpUrl = apiCtx.url;
          message.failedHttpRequestBodyFull = stringifyRequestBodyFull(
            apiCtx.body
          );
        }

        api.Reporting.reportBug(message)
          .then(() => {
            logger.log('bug submitted successfully');
            resolve(true);
          })
          .catch(err => {
            logger.warn('error submitting bug report', err);
            resolve(false);
          });
      } catch (err) {
        logger.error('caught error submitting report', err);
        resolve(false);
      }
    });
  }
};

export default errorReportHelper;
